import { byClose, isNearCap, liveAt, WEEK, type HomeRaise } from './live';

/**
 * The hero's Weekly Sonar card: when the next issue lands, the countdown to
 * it, and three tiles teasing what it will hold. Pure, so the server's first
 * paint and the browser's ticks agree. The tiles count the live raises
 * (live.ts), never the sample deals; the issue's date is on the real clock,
 * as the sign-up's "first issue" is (public.controller.ts).
 */

/** "Every Monday at 08:00 your time": src/email/digest.ts sends it then. */
export const ISSUE_WEEKDAY = 1;
export const ISSUE_HOUR = 8;

/** Where the invite link sends a friend: the card, by way of a redirect. */
export const INVITE_PATH = '/weekly';

/** How long "Link copied ✓" stays before the button says its label again. */
export const INVITE_RESET_MS = 2_400;

const WEEKDAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
const MONTHS = 'Jan Feb Mar Apr May Jun Jul Aug Sep Oct Nov Dec'.split(' ');

/** A moment on a clock; no zone is the runtime's own, the browser's. */
function civil(at: number, timeZone?: string) {
  const f: Record<string, string> = {};
  for (const p of new Intl.DateTimeFormat('en-US', {
    timeZone,
    year: 'numeric',
    month: 'numeric',
    day: 'numeric',
    hour: 'numeric',
    minute: 'numeric',
    second: 'numeric',
    weekday: 'short',
    hourCycle: 'h23',
  }).formatToParts(at)) {
    f[p.type] = p.value;
  }
  return {
    year: Number(f.year),
    month: Number(f.month),
    day: Number(f.day),
    hour: Number(f.hour),
    minute: Number(f.minute),
    second: Number(f.second),
    weekday: WEEKDAYS.indexOf(f.weekday),
  };
}

/** The instant a wall-clock time (as UTC ms) falls on, across a change of clocks. */
function instant(wall: number, timeZone?: string): number {
  const offset = (t: number) => {
    const c = civil(t, timeZone);
    return (
      Date.UTC(c.year, c.month - 1, c.day, c.hour, c.minute, c.second) -
      Math.floor(t / 1000) * 1000
    );
  };
  return wall - offset(wall - offset(wall));
}

export interface Issue {
  /** When it lands. */
  at: number;
  /** "Mon 19 Oct", on the same clock. */
  day: string;
}

/**
 * The next Weekly Sonar on a clock: today if it is Monday before 08:00
 * there, else the Monday after. The same rule as firstIssue() in
 * src/email/digest.ts, which the browser cannot import.
 */
export function nextIssue(now: number, timeZone?: string): Issue {
  const c = civil(now, timeZone);
  let ahead = (ISSUE_WEEKDAY - c.weekday + 7) % 7;
  if (ahead === 0 && c.hour >= ISSUE_HOUR) ahead = 7;
  const day = new Date(Date.UTC(c.year, c.month - 1, c.day + ahead));
  return {
    at: instant(
      Date.UTC(c.year, c.month - 1, c.day + ahead, ISSUE_HOUR),
      timeZone,
    ),
    day: `${WEEKDAYS[day.getUTCDay()]} ${day.getUTCDate()} ${MONTHS[day.getUTCMonth()]}`,
  };
}

const pad = (n: number) => String(n).padStart(2, '0');

/**
 * "2d 19h 59m 55s". Hours, minutes and seconds keep two digits, so the
 * line is as wide at every tick.
 */
export function landsIn(ms: number): string {
  const s = Math.max(0, Math.floor(ms / 1000));
  const d = Math.floor(s / 86_400);
  const h = Math.floor((s % 86_400) / 3600);
  const m = Math.floor((s % 3600) / 60);
  return `${d}d ${pad(h)}h ${pad(m)}m ${pad(s % 60)}s`;
}

export type TeaserId = 'new' | 'closing' | 'cap';

export interface TeaserTile {
  id: TeaserId;
  count: number;
  label: string;
  /** Named in plain text. */
  first: string | null;
  /** Named, blurred. */
  second: string | null;
  /** "+N": the ones after the second. */
  more: number;
  /** What a screen reader hears after the first: "and N more". */
  others: number;
}

const LABELS: Record<TeaserId, [one: string, many: string]> = {
  new: ['new raise', 'new raises'],
  closing: ['closing this week', 'closing this week'],
  cap: ['near its cap', 'near their cap'],
};

/**
 * The three tiles, as the Weekly Sonar would sum the week up now
 * (weeklyDigest in src/email/digest.ts): live raises that launched in the
 * last 7 days (newest first), those closing within 7 days and those near
 * their cap (both soonest first, as the email lists them).
 */
export function weeklyTeaser(
  raises: readonly HomeRaise[],
  now: number,
): TeaserTile[] {
  const live = liveAt(raises, now).sort(byClose);
  const launched = (r: HomeRaise) => r.launchedAt ?? 0;
  const lists: [TeaserId, HomeRaise[]][] = [
    [
      'new',
      live
        .filter((r) => r.launchedAt !== null && now - r.launchedAt <= WEEK)
        .sort((a, b) => launched(b) - launched(a) || byClose(a, b)),
    ],
    ['closing', live.filter((r) => r.closesAt - now <= WEEK)],
    ['cap', live.filter(isNearCap)],
  ];
  return lists.map(([id, list]) => ({
    id,
    count: list.length,
    label: LABELS[id][list.length === 1 ? 0 : 1],
    first: list[0]?.name ?? null,
    second: list[1]?.name ?? null,
    more: Math.max(0, list.length - 2),
    others: Math.max(0, list.length - 1),
  }));
}
